/**
 * `LookupRefreshContext` — page-scoped trigger for silently refetching
 * every mounted `<LookupSelect>`'s options.
 *
 * Mario-driven post-phase-32b (for the since-retired `/actions` page,
 * d7c697dc): when a mounted `<DispatchForm>` (today the `/status` "Run
 * with data…" modal) dispatches a mutation (e.g. `staff_create_member`),
 * other mounted lookup dropdowns (e.g. `book_session`'s `member_id`)
 * should reflect the new row without the operator pressing the global
 * Refresh button + reopening the dropdown.
 *
 * The provider exposes a monotonically-incrementing `refreshKey` +
 * `bump()` action. `LookupSelect` adds `refreshKey` to its `useEffect`
 * deps so a bump triggers a silent refetch (no "Loading…" placeholder
 * after the initial load — see the `initialLoadRef` guard there).
 *
 * Outside a provider (e.g. if `<SchemaForm>` is ever mounted in a page
 * that hasn't wrapped its tree), `bump()` is a no-op + `refreshKey`
 * stays at 0 so the fetch-once-on-mount behaviour is preserved.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { invalidate } from "./resource-cache";

interface LookupRefreshValue {
  readonly refreshKey: number;
  readonly bump: () => void;
}

const LookupRefreshContext = createContext<LookupRefreshValue>({
  refreshKey: 0,
  bump: () => {},
});

export function LookupRefreshProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const bump = useCallback((): void => {
    // Phase 67 — a lookup refresh now invalidates every cached
    // `lookup:<entity>` resource so mounted `<LookupSelect>`s revalidate
    // through the shared cache. `refreshKey` is still bumped for any
    // consumer that depends on it directly.
    invalidate("lookup:");
    setRefreshKey((k) => k + 1);
  }, []);
  const value = useMemo<LookupRefreshValue>(
    () => ({ refreshKey, bump }),
    [refreshKey, bump],
  );
  return (
    <LookupRefreshContext.Provider value={value}>
      {children}
    </LookupRefreshContext.Provider>
  );
}

export function useLookupRefresh(): LookupRefreshValue {
  return useContext(LookupRefreshContext);
}
